/**
 * ApiProvider — injects the API classes into the React tree.
 *
 * Apps wire this at entry (T7). Everything downstream consumes via the
 * `useApi` hook so tests can stand in a mock HttpClient trivially.
 *
 * This file lives in shared because the hook surface must be identical on
 * web and native. React itself is platform-agnostic.
 */

import { createContext, useContext, type ReactNode } from 'react';
import type { IStorage } from '@shopista/storage/interface';
import type {
  AuthApi,
  HouseholdsApi,
  HouseholdProductsApi,
  ShoppingListsApi,
  RetailerOffersApi,
  FlyerDealsApi,
  StoresApi,
} from '../index';

export interface ApiBundle {
  auth: AuthApi;
  households: HouseholdsApi;
  ShoppingLists: ShoppingListsApi;
  HouseholdProducts: HouseholdProductsApi;
  RetailerOffers: RetailerOffersApi;
  FlyerDeals: FlyerDealsApi;
  stores: StoresApi;
}

export interface ReadPathContextValue {
  isOnline: () => boolean;
}

const ApiContext = createContext<ApiBundle | null>(null);
const StorageContext = createContext<IStorage | null>(null);

// `null` means no read-path props were supplied. Legacy callers then retain
// the online default rather than receiving a synthetic connectivity value.
const ReadPathContext = createContext<ReadPathContextValue | null>(null);

export function ApiProvider(props: {
  api: ApiBundle;
  storage?: IStorage;
  isOnline?: () => boolean;
  children: ReactNode;
}) {
  const readPath: ReadPathContextValue | null =
    props.isOnline !== undefined
      ? {
          isOnline: props.isOnline,
        }
      : null;
  return (
    <ApiContext.Provider value={props.api}>
      <StorageContext.Provider value={props.storage ?? null}>
        <ReadPathContext.Provider value={readPath}>{props.children}</ReadPathContext.Provider>
      </StorageContext.Provider>
    </ApiContext.Provider>
  );
}

export function useApi(): ApiBundle {
  const ctx = useContext(ApiContext);
  if (!ctx) {
    throw new Error(
      'useApi() called outside ApiProvider. Wrap your app in <ApiProvider api={...}> at entry (T7).',
    );
  }
  return ctx;
}

export function useStorage(): IStorage | null {
  return useContext(StorageContext);
}

export function useReadPathContext(): ReadPathContextValue | null {
  return useContext(ReadPathContext);
}
